import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import Swal from "sweetalert2";
import {
  getFaqs,
  addFaq,
  updateFaq,
  deleteFaq,
  toggleFaqStatus,
} from "../../api/ApiServices";
import {
  FaEdit,
  FaEllipsisV,
  FaPause,
  FaPlay,
  FaTrash,
  FaPlus,
  FaQuestionCircle,
  FaSearch,
} from "react-icons/fa";

const ITEMS_PER_PAGE = 10;

const FaqManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);

  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  const [validationErrors, setValidationErrors] = useState({
    question: "",
    answer: "",
  });
  const [touched, setTouched] = useState({
    question: false,
    answer: false,
  });

  /* =====================================================
     FETCH FAQS
  ===================================================== */
  const fetchFaqs = async (page = 1, search = "") => {
    setLoading(true);

    try {
      const res = await getFaqs(page, ITEMS_PER_PAGE, search);

      if (res && res.success) {
        setFaqs(res.data || []);
        setTotalItems(res.pagination?.totalRecords || 0);
        setTotalPages(res.pagination?.totalPages || 1);
        setCurrentPage(res.pagination?.currentPage || 1);
      } else {
        setFaqs([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      Swal.fire({
        title: "Error",
        text: error?.message || "Failed to load FAQs",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs(1, searchTerm);
  }, [searchTerm]);

  /* =====================================================
     VALIDATION FUNCTIONS
  ===================================================== */
  const validateQuestion = (question) => {
    const trimmed = question?.trim() || "";
    if (!trimmed) {
      return "Question is required";
    }
    if (trimmed.length < 3) {
      return "Question must be at least 3 characters long";
    }
    if (trimmed.length > 300) {
      return "Question cannot exceed 300 characters";
    }
    return "";
  };

  const validateAnswer = (answer) => {
    const trimmed = answer?.trim() || "";
    if (!trimmed) {
      return "Answer is required";
    }
    if (trimmed.length < 3) {
      return "Answer must be at least 3 characters long";
    }
    return "";
  };

  const validateForm = () => {
    const questionError = validateQuestion(formData.question);
    const answerError = validateAnswer(formData.answer);

    setValidationErrors({
      question: questionError,
      answer: answerError,
    });
    setTouched({
      question: true,
      answer: true,
    });

    return !questionError && !answerError;
  };

  const handleFieldBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === "question") {
      const error = validateQuestion(formData.question);
      setValidationErrors((prev) => ({ ...prev, question: error }));
    }
    if (field === "answer") {
      const error = validateAnswer(formData.answer);
      setValidationErrors((prev) => ({ ...prev, answer: error }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (touched[field]) {
      if (field === "question") {
        setValidationErrors((prev) => ({
          ...prev,
          question: validateQuestion(value),
        }));
      }
      if (field === "answer") {
        setValidationErrors((prev) => ({
          ...prev,
          answer: validateAnswer(value),
        }));
      }
    }
  };

  /* =====================================================
     MODAL CONTROLS
  ===================================================== */
  const openCreateModal = () => {
    setEditMode(false);
    setEditingUuid(null);
    setFormData({ question: "", answer: "" });
    setValidationErrors({ question: "", answer: "" });
    setTouched({ question: false, answer: false });
    setModalOpen(true);
  };

  const openEditModal = (faqItem) => {
    setEditMode(true);
    setEditingUuid(faqItem.uuid);
    setFormData({
      question: faqItem.question || "",
      answer: faqItem.answer || "",
    });
    setValidationErrors({ question: "", answer: "" });
    setTouched({ question: false, answer: false });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setEditingUuid(null);
    setFormData({ question: "", answer: "" });
    setValidationErrors({ question: "", answer: "" });
    setTouched({ question: false, answer: false });
  };

  /* =====================================================
     SAVE (CREATE / UPDATE) FAQ
  ===================================================== */
  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (editMode && editingUuid) {
        const res = await updateFaq(editingUuid, {
          question: formData.question.trim(),
          answer: formData.answer.trim(),
        });

        if (res?.success) {
          Swal.fire({
            title: "Updated!",
            text: res.message || "FAQ updated successfully",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          closeModal();
          fetchFaqs(currentPage, searchTerm);
        } else {
          Swal.fire({
            title: "Error",
            text: res?.message || "Failed to update FAQ",
            icon: "error",
          });
        }
      } else {
        const res = await addFaq({
          question: formData.question.trim(),
          answer: formData.answer.trim(),
        });

        if (res?.success) {
          Swal.fire({
            title: "Created!",
            text: res.message || "FAQ added successfully",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          closeModal();
          fetchFaqs(1, searchTerm);
        } else {
          Swal.fire({
            title: "Error",
            text: res?.message || "Failed to add FAQ",
            icon: "error",
          });
        }
      }
    } catch (error) {
      console.error("Save FAQ Error:", error);
      Swal.fire({
        title: "Error",
        text: error?.message || "Something went wrong while saving FAQ",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     TOGGLE FAQ STATUS
  ===================================================== */
  const handleToggleStatus = async (uuid) => {
    try {
      const res = await toggleFaqStatus(uuid);
      if (res?.success) {
        Swal.fire({
          title: "Status Changed",
          text: res.message || "FAQ status updated",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        fetchFaqs(currentPage, searchTerm);
      } else {
        Swal.fire("Error", res?.message || "Failed to toggle status", "error");
      }
    } catch (error) {
      console.error("Toggle status error:", error);
      Swal.fire("Error", error?.message || "Failed to toggle status", "error");
    }
  };

  /* =====================================================
     DELETE FAQ
  ===================================================== */
  const handleDelete = async (uuid) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this FAQ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const res = await deleteFaq(uuid);
        if (res?.success) {
          Swal.fire({
            title: "Deleted!",
            text: res.message || "FAQ has been deleted.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          fetchFaqs(currentPage, searchTerm);
        } else {
          Swal.fire("Error", res?.message || "Failed to delete FAQ", "error");
        }
      } catch (error) {
        console.error("Delete FAQ error:", error);
        Swal.fire("Error", error?.message || "Failed to delete FAQ", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  /* =====================================================
     PAGINATION HANDLER
  ===================================================== */
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      fetchFaqs(page, searchTerm);
    }
  };

  return (
    <>
      <CommonHeader />

      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow border-0 rounded-lg">
              {/* CARD HEADER */}
              <CardHeader className="bg-transparent border-0 d-flex flex-column flex-md-row align-items-md-center justify-content-between py-4">
                <div>
                  <h3 className="mb-0 text-primary font-weight-bold d-flex align-items-center">
                    <FaQuestionCircle className="mr-2 text-info" /> FAQ Management
                  </h3>
                  <p className="text-muted small mb-0 mt-1">
                    Manage Frequently Asked Questions for travelers and guides.
                  </p>
                </div>

                <div className="mt-3 mt-md-0">
                  <Button
                    color="primary"
                    className="btn-icon btn-3 shadow-sm px-4"
                    onClick={openCreateModal}
                  >
                    <span className="btn-inner--icon">
                      <FaPlus className="mr-2" />
                    </span>
                    <span className="btn-inner--text font-weight-bold">
                      Add New FAQ
                    </span>
                  </Button>
                </div>
              </CardHeader>

              {/* CARD BODY (SEARCH & TABLE) */}
              <CardBody className="pt-0">
                {/* SEARCH INPUT */}
                <Row className="mb-4">
                  <Col md="6" lg="4">
                    <InputGroup className="input-group-alternative input-group-merge shadow-sm">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <FaSearch className="text-muted" />
                        </InputGroupText>
                      </InputGroupAddon>

                      <Input
                        placeholder="Search FAQs by question..."
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      {searchTerm && (
                        <InputGroupAddon addonType="append">
                          <InputGroupText
                            style={{ cursor: "pointer" }}
                            onClick={() => setSearchTerm("")}
                          >
                            <i className="fas fa-times" />
                          </InputGroupText>
                        </InputGroupAddon>
                      )}
                    </InputGroup>
                  </Col>
                </Row>

                {/* TABLE */}
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                  <Table className="align-items-center table-flush hover" hover>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "5%" }}>Sr. No.</th>
                        <th style={{ width: "35%" }}>Question</th>
                        <th style={{ width: "40%" }}>Answer</th>
                        <th style={{ width: "10%" }}>Status</th>
                        <th style={{ width: "10%" }} className="text-right">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading && faqs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-5">
                            <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                            <p className="mt-2">Loading FAQs...</p>
                          </td>
                        </tr>
                      ) : faqs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-5">
                            <FaQuestionCircle className="text-muted mb-3" size={40} />
                            <h5>No FAQs Found</h5>
                            <p className="text-muted mb-3">
                              {searchTerm
                                ? "No FAQs matched your search terms."
                                : "No FAQs have been added yet."}
                            </p>
                            {!searchTerm && (
                              <Button
                                color="primary"
                                size="sm"
                                onClick={openCreateModal}
                              >
                                <FaPlus className="mr-2" /> Add Your First FAQ
                              </Button>
                            )}
                          </td>
                        </tr>
                      ) : (
                        faqs.map((faqItem, index) => (
                          <tr key={faqItem.uuid || faqItem._id}>
                            {/* INDEX */}
                            <td>
                              <span className="font-weight-bold text-muted">
                                {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                              </span>
                            </td>

                            {/* QUESTION */}
                            <td>
                              <div className="font-weight-bold text-dark text-wrap" style={{ maxWidth: "350px" }}>
                                {faqItem.question}
                              </div>
                            </td>

                            {/* ANSWER */}
                            <td>
                              <div
                                className="text-muted text-wrap small"
                                style={{
                                  maxWidth: "450px",
                                  maxHeight: "80px",
                                  overflowY: "auto",
                                  whiteSpace: "pre-line",
                                }}
                              >
                                {faqItem.answer}
                              </div>
                            </td>

                            {/* STATUS */}
                            <td>
                              <Badge
                                color={faqItem.isActive ? "success" : "secondary"}
                                pill
                                style={{ cursor: "pointer", padding: "6px 12px" }}
                                onClick={() => handleToggleStatus(faqItem.uuid)}
                              >
                                <i className="fas fa-circle mr-1" />
                                {faqItem.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </td>

                            {/* ACTIONS */}
                            <td className="text-right">
                              <UncontrolledDropdown>
                                <DropdownToggle
                                  className="btn-icon-only text-light"
                                  color=""
                                  role="button"
                                  size="sm"
                                >
                                  <FaEllipsisV />
                                </DropdownToggle>

                                <DropdownMenu className="dropdown-menu-arrow" right>
                                  <DropdownItem onClick={() => openEditModal(faqItem)}>
                                    <FaEdit className="mr-2 text-primary" />
                                    Edit FAQ
                                  </DropdownItem>

                                  <DropdownItem onClick={() => handleToggleStatus(faqItem.uuid)}>
                                    {faqItem.isActive ? (
                                      <>
                                        <FaPause className="mr-2 text-warning" />
                                        Deactivate
                                      </>
                                    ) : (
                                      <>
                                        <FaPlay className="mr-2 text-success" />
                                        Activate
                                      </>
                                    )}
                                  </DropdownItem>

                                  <DropdownItem onClick={() => handleDelete(faqItem.uuid)}>
                                    <FaTrash className="mr-2 text-danger" />
                                    Delete FAQ
                                  </DropdownItem>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>

              {/* CARD FOOTER (UNIFIED PAGINATION) */}
              {!loading && totalItems > 0 && (
                <CardFooter className="py-4 border-0">
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={handlePageChange}
                    itemName="FAQs"
                  />
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* CREATE / EDIT MODAL */}
      <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
        <ModalHeader toggle={closeModal} className="bg-light">
          <span className="font-weight-bold text-primary">
            {editMode ? "Edit FAQ" : "Add New FAQ"}
          </span>
        </ModalHeader>

        <Form onSubmit={handleSave}>
          <ModalBody className="py-4">
            <FormGroup>
              <Label className="font-weight-bold small text-uppercase">
                Question <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter the question..."
                value={formData.question}
                onChange={(e) => handleFieldChange("question", e.target.value)}
                onBlur={() => handleFieldBlur("question")}
                invalid={touched.question && !!validationErrors.question}
              />
              {touched.question && validationErrors.question && (
                <div className="text-danger small mt-1">
                  {validationErrors.question}
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label className="font-weight-bold small text-uppercase">
                Answer <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                rows="5"
                placeholder="Enter the detailed answer..."
                value={formData.answer}
                onChange={(e) => handleFieldChange("answer", e.target.value)}
                onBlur={() => handleFieldBlur("answer")}
                invalid={touched.answer && !!validationErrors.answer}
              />
              {touched.answer && validationErrors.answer && (
                <div className="text-danger small mt-1">
                  {validationErrors.answer}
                </div>
              )}
            </FormGroup>
          </ModalBody>

          <ModalFooter className="bg-light py-3">
            <Button color="secondary" outline onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2" /> Saving...
                </>
              ) : editMode ? (
                "Update FAQ"
              ) : (
                "Create FAQ"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default FaqManagement;
